import React from 'react'
import Title from 'antd/es/typography/Title'
import { Button, Empty, Flex, List, Space } from 'antd'
import Avatar from '../../../../components/common/avatar/Avatar'
import { useParams } from 'react-router'
import { useFetchSubmissionReviewsQuery } from '../../../../store/apis/submission.api'
import { getColorByIndex } from '../../../../utils/colors.helper'
import { useSelector } from 'react-redux'
import { fetchSubmissionReviewsSelectors } from '../../../../store/apis/utils/selectors/submission.api.selectors'
import submissionSelectors from '../../../../store/selectors/submission.selectors'
import { sqlToJsDate } from '../../../../utils/date.helper'
import Text from 'antd/es/typography/Text'
import PatternIcon from '../../../../components/common/PatternIcon'
import { ArrowRightOutlined } from '@ant-design/icons'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import { AssignmentPermissions, Forbiddances } from '../../../../constants/assignment-permissions'
import Access from '../access/Access'

const SubmissionReviewsList = ({ setRereviewMode }) => {
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
    const patterns = useSelector(patternSelectors.all)

    const { token, layerId } = useParams()
    const { data: reviews, isFetching: isFetchingReviews } = useFetchSubmissionReviewsQuery(
        { token, layerId },
        {
            selectFromResult: result => fetchSubmissionReviewsSelectors.reviewsById({ id: openedSubmissionId }, result),
        },
    )

    const header = (
        <Flex justify='space-between'>
            <Title level={4} style={{ margin: 0 }}>
                Reviews
            </Title>
            <Access
                permissions={[AssignmentPermissions.CAN_EDIT]}
                skipForbiddances={[
                    Forbiddances.IS_VERSION_PUBLISHED,
                    Forbiddances.IS_MBA,
                    Forbiddances.IS_IN_CONFIRM_CHANGES_MODE,
                    Forbiddances.VERSION_HAS_MBAS,
                    Forbiddances.IS_VERSION_NOT_LATEST,
                    Forbiddances.IS_PROCESSING,
                ]}
            >
                <Button type='primary' onClick={() => setRereviewMode(true)}>
                    Re-review
                </Button>
            </Access>
        </Flex>
    )

    return (
        <List
            header={header}
            dataSource={reviews}
            loading={{ spinning: isFetchingReviews, tip: 'Loading reviews...' }}
            locale={{
                emptyText: <Empty description="The submission wasn't reviewed by anyone yet" />,
            }}
            renderItem={review => {
                const oldPattern = patterns.find(pattern => pattern.motif_id === review.oldPatternId)
                const newPattern = patterns.find(pattern => pattern.motif_id === review.newPatternId)
                const patternChanged = review.oldPatternId !== review.newPatternId
                const reasoningChanged = review.oldReasoning !== review.newReasoning

                let userActionDescription = 'corrected '
                if (patternChanged) userActionDescription += 'the pattern'
                if (patternChanged && reasoningChanged) userActionDescription += ' and'
                if (reasoningChanged) userActionDescription += ' the reasoning'
                if (!patternChanged && !reasoningChanged) userActionDescription = 'approved'

                return (
                    <List.Item key={review.reviewer.id + review.reviewedAt}>
                        <Flex vertical gap={8}>
                            <div>
                                <Avatar
                                    name={review.reviewer.name}
                                    color={getColorByIndex(review.reviewer.id).value}
                                    className='mr-8'
                                />
                                <Text strong>{review.reviewer.name}</Text> {userActionDescription} at{' '}
                                <Text type='secondary'>{sqlToJsDate(review.reviewedAt)}</Text>
                            </div>
                            {patternChanged && (
                                <div className='ml-4'>
                                    <Space size={4}>
                                        <PatternIcon id={review.oldPatternId} />
                                        {oldPattern?.motif_caption || 'None'} <ArrowRightOutlined />{' '}
                                        <PatternIcon id={review.newPatternId} /> {newPattern?.motif_caption || 'None'}
                                    </Space>
                                </div>
                            )}
                            {reasoningChanged && (
                                <div className='ml-4'>
                                    <Space size={4}>
                                        {review.oldReasoning || 'None'} <ArrowRightOutlined />{' '}
                                        {review.newReasoning || 'None'}
                                    </Space>
                                </div>
                            )}
                        </Flex>
                    </List.Item>
                )
            }}
        />
    )
}

export default SubmissionReviewsList
